/* eslint-disable react-hooks/exhaustive-deps */
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  HStack,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import moment from "moment-timezone";
import { useMemo } from "react";
import { FaSortAlphaDown, FaSortAlphaDownAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetCodes } from "../../../hooks/useGetCodes";
import LoadingSpinner from "../../../LoadingSpinner";


const EdssResultTableForLink = ({ searchResult, isResultLoading }) => {
  const { id, personID } = useParams();
  const { data } = useGetCodes("LINK_TYPES");

  const linkTypeSelectOptions =
    data?.CONFIG?.map((opt) => ({
      value: opt.CODE,
      label: opt.TEXT,
    })) || [];

  const handleUseExistingCase = (caseData, linkType) => {
    const post = {
      LINK_TYPE: linkType,
      SOURCE_CASE: { id: id, PERSON_ID: personID },
      EVENT_DATE: caseData?.EVENT_DATE,
      TARGET_CASE: {
        id: caseData?.id,
        PERSON_ID: caseData?.PERSON_ID,
      },
      MODE: "EXISTING_PERSON_EXISTING_CASE",
    };

    fetch("/api/createLink", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(post),
    })
      .then((data) => {
        if (data?.status === 200) {
          toast.success("Link created successfully");
          window.location.reload();
        } else if (data?.status === 400) {
          toast.error("Case Link Already Exists!");
        } else {
          toast.error("Something went wrong!");
        }
      })
      .catch(() => toast.error("Something went wrong"));
  };

  const handleCreateNewCaseLink = (caseData, linkType) => {
    const post = {
      LINK_TYPE: linkType,
      SOURCE_CASE: { id: id, PERSON_ID: personID },
      PERSON: { id: caseData?.PERSON_ID },
      EVENT_DATE: caseData?.EVENT_DATE,
      MODE: "EXISTING_PERSON_NEW_CASE",
    };

    fetch("/api/createLink", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(post),
    })
      .then((data) => {
        if (data?.status === 200) {
          toast.success("Link created successfully");
          window.location.reload();
        } else if (data?.status === 400) {
          toast.error("Case Link Already Exists!");
        } else {
          toast.error("Something went wrong!");
        }
      })
      .catch(() => toast.error("Something went wrong"));
  };

  const columns = useMemo(
    () => [
      {
        header: "First Name",
        accessorFn: (row) => `${row.FIRST_NAME || ""}`,
      },
      {
        header: "Last Name",
        accessorFn: (row) => `${row.LAST_NAME || ""}`,
      },
      {
        header: "Birth Date",
        accessorFn: (row) =>
          `${moment(row?.BIRTH_DATE, "YYYYMMDD").format("YYYY-MM-DD") || ""}`,
      },
      { accessorKey: "GENDER", header: "Gender" },
      { accessorKey: "PRODUCT", header: "Product" },
      {
        header: "Event Date",
        accessorFn: (row) =>
          `${moment(row?.EVENT_DATE, "YYYYMMDD").format("YYYY-MM-DD") || ""}`,
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => (
          <Menu closeOnSelect={false}>
            <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />}>
              Select
            </MenuButton>
            <MenuList>
              <MenuGroup title="Use Existing Case">
                <Box maxH="100px" overflowY="auto">
                  <MenuOptionGroup
                    type="radio"
                    onChange={(value) =>
                      handleUseExistingCase(row.original, value)
                    }
                  >
                    {linkTypeSelectOptions?.map((item, idx) => (
                      <MenuItemOption value={item.value} key={idx}>
                        {item.label}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </Box>
              </MenuGroup>

              <Divider />

              <MenuGroup title="Create New Case and Link">
                <Box maxH="100px" overflowY="auto">
                  <MenuOptionGroup
                    type="radio"
                    onChange={(value) =>
                      handleCreateNewCaseLink(row.original, value)
                    }
                  >
                    {linkTypeSelectOptions?.map((item, idx) => (
                      <MenuItemOption value={item.value} key={idx}>
                        {item.label}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </Box>
              </MenuGroup>
            </MenuList>
          </Menu>
        ),
      },
    ],
    [linkTypeSelectOptions]
  );

  const table = useReactTable({
    data: searchResult,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 8 } },
  });

  if (isResultLoading) return <LoadingSpinner />;

  return (
    <Box p={1}>
      <Box minH="65vh" maxH="73vh" overflowY="scroll">
        <Table variant="simple" size="sm">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    cursor="pointer"
                  >
                    <HStack spacing="2px">
                      <Text fontSize="sm">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Text>
                      {header.column.getIsSorted() === "asc" ? (
                        <FaSortAlphaDown />
                      ) : header.column.getIsSorted() === "desc" ? (
                        <FaSortAlphaDownAlt />
                      ) : null}
                    </HStack>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <HStack justifyContent="space-between" mt={1}>
        <Button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          size="sm"
        >
          Previous
        </Button>
        <Text>
          Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
          {table.getPageCount()}
        </Text>
        <Button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          size="sm"
          bg="bgColor"
          color="white"
          _hover={{ background: "#3c1514" }}
          _disabled={{ background: "#3c1514" }}
        >
          Next
        </Button>
      </HStack>
    </Box>
  );
};

export default EdssResultTableForLink;
